import { useState, useEffect, useMemo } from 'react';
import {
  Activity, ArrowRight, ArrowLeft, Check, CheckCircle2, Loader2, ChevronRight,
  Mail, User, Building, TrendingUp, TrendingDown, Calculator, Info, Download,
  Users, Euro, Heart, AlertTriangle, Sparkles, ChevronsUpDown, Search, X,
  BarChart3, Target, Award
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import {
  RadialBarChart, RadialBar, PolarAngleAxis, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, Tooltip, Cell, CartesianGrid
} from 'recharts';
import { vitaliteitBenchmarkAPI } from '@/lib/api';
import { generateBenchmarkPdf } from '@/lib/pdfGenerator';
import BookingCta from '@/components/BookingCta';

const LOGO_ICON = 'https://customer-assets.emergentagent.com/job_buyer-match-26/artifacts/52px3160_zynq_logo_icon_e0dde9b0559f42738516da0422c8a0d0.png';

// Fallback benchmark-data (wordt overschreven door /config van backend)
const FALLBACK_BENCHMARKS = {
  "A: Landbouw, bosbouw en visserij": { verzuim: 3.4, budget: 140, burnout: 11, dagKosten: 340 },
  "B: Delfstoffenwinning": { verzuim: 4.8, budget: 550, burnout: 15, dagKosten: 480 },
  "C: Industrie": { verzuim: 6.2, budget: 240, burnout: 15, dagKosten: 395 },
  "D: Energievoorziening": { verzuim: 5.2, budget: 410, burnout: 14, dagKosten: 460 },
  "E: Water- en afvalbeheer": { verzuim: 6.8, budget: 280, burnout: 16, dagKosten: 370 },
  "F: Bouwnijverheid": { verzuim: 5.4, budget: 210, burnout: 12, dagKosten: 415 },
  "G: Groot- en detailhandel": { verzuim: 4.9, budget: 170, burnout: 17, dagKosten: 335 },
  "H: Vervoer en opslag": { verzuim: 6.1, budget: 190, burnout: 15, dagKosten: 365 },
  "I: Horeca": { verzuim: 4.3, budget: 120, burnout: 28, dagKosten: 310 },
  "J: Informatie en communicatie": { verzuim: 4.1, budget: 490, burnout: 20, dagKosten: 440 },
  "K: Financiële instellingen": { verzuim: 3.7, budget: 630, burnout: 18, dagKosten: 470 },
  "L: Onroerend goed": { verzuim: 3.9, budget: 380, burnout: 14, dagKosten: 410 },
  "M: Specialistische zakelijke diensten": { verzuim: 3.8, budget: 520, burnout: 17, dagKosten: 450 },
  "N: Verhuur en overige zakelijke diensten": { verzuim: 6.5, budget: 160, burnout: 19, dagKosten: 320 },
  "O: Overheid en defensie": { verzuim: 6.9, budget: 410, burnout: 22, dagKosten: 415 },
  "P: Onderwijs": { verzuim: 7.6, budget: 320, burnout: 26, dagKosten: 385 },
  "Q: Gezondheids- en welzijnszorg": { verzuim: 8.2, budget: 270, burnout: 25, dagKosten: 365 },
  "R: Cultuur, sport en recreatie": { verzuim: 4.5, budget: 230, burnout: 21, dagKosten: 345 },
  "S: Overige dienstverlening": { verzuim: 5.1, budget: 190, burnout: 18, dagKosten: 330 },
  "T: Huishoudens als werkgever": { verzuim: 3.2, budget: 100, burnout: 10, dagKosten: 290 },
  "U: Extraterritoriale organisaties": { verzuim: 3.5, budget: 700, burnout: 19, dagKosten: 500 },
};

const FALLBACK_CONFIG = {
  active: true,
  intro_title: 'Hoe vitaal is jouw organisatie?',
  intro_subtitle: 'Vergelijk in 3 minuten jouw verzuim en vitaliteitsbudget met het sectorgemiddelde op basis van de meest recente TNO/CBS SBI-cijfers.',
  intro_badge: 'Nieuw · Benchmark editie 2026',
  cta_start: 'Start de benchmark',
  cta_submit: 'Toon resultaten',
  accent_color: '#10b981',
  show_pdf_download: true,
  roi_label: 'Jaarlijkse potentiële besparing',
  working_days_per_year: 230,
  fte_help_title: 'Hulp bij berekenen FTE',
  fte_help_explainer: 'FTE staat voor Full-Time Equivalent: het totale aantal contracturen van al je medewerkers, gedeeld door de duur van een volledige werkweek (meestal 40 uur).',
  thank_you_message: 'Bedankt! We hebben je benchmark-aanvraag ontvangen.',
  benchmarks: FALLBACK_BENCHMARKS,
};

// Format helpers
const fmtCur = (n) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(Math.round(n || 0));
const fmtPct = (n) => `${(Number(n) || 0).toFixed(1)}%`;
const fmtNum = (n) => new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 1 }).format(n || 0);

// ──────────────────────────────────────────────────────────────────────────────
export default function VitaliteitBenchmarkPage() {
  const [step, setStep] = useState(0); // 0 intro, 1 company, 2 metrics, 3 contact, 4 dashboard
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submissionId, setSubmissionId] = useState(null);

  // Remote config (intro tekst, kleuren, sector overrides, werkdagen…)
  const [cfg, setCfg] = useState(FALLBACK_CONFIG);
  const [cfgLoaded, setCfgLoaded] = useState(false);

  // Step 1
  const [company, setCompany] = useState('');
  const [sector, setSector] = useState('');
  const [extraSectors, setExtraSectors] = useState([]);
  const [fte, setFte] = useState('');

  // Step 2
  const [verzuim, setVerzuim] = useState('');
  const [budget, setBudget] = useState('');

  // Step 3
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');

  // Modals
  const [sectorOpen, setSectorOpen] = useState(false);
  const [extraSectorsOpen, setExtraSectorsOpen] = useState(false);
  const [fteHelpOpen, setFteHelpOpen] = useState(false);

  // Force light mode for embeddable consistency
  useEffect(() => {
    const root = document.documentElement;
    const hadDark = root.classList.contains('dark');
    root.classList.remove('dark');
    return () => { if (hadDark) root.classList.add('dark'); };
  }, []);

  // Fetch config (titels, accent, sector overrides) — public endpoint
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await vitaliteitBenchmarkAPI.getConfig();
        if (!active) return;
        const data = res.data || {};
        setCfg({
          ...FALLBACK_CONFIG,
          ...data,
          benchmarks: (data.benchmarks && Object.keys(data.benchmarks).length)
            ? data.benchmarks
            : FALLBACK_BENCHMARKS,
        });
      } catch (e) {
        // niet kritiek — fallback gebruikt
      } finally {
        if (active) setCfgLoaded(true);
      }
    })();
    return () => { active = false; };
  }, []);

  const benchmarks = cfg.benchmarks || FALLBACK_BENCHMARKS;
  const sectorKeys = useMemo(() => Object.keys(benchmarks), [benchmarks]);
  const sectorBenchmark = useMemo(() => (sector ? benchmarks[sector] : null), [sector, benchmarks]);
  const extraBenchmarks = useMemo(
    () => extraSectors.map(s => ({ sector: s, ...benchmarks[s] })).filter(x => x.verzuim != null),
    [extraSectors, benchmarks]
  );
  const accent = cfg.accent_color || '#10b981';
  const workingDays = Number(cfg.working_days_per_year) || 230;

  const fteNum = parseFloat(fte) || 0;
  const verzuimNum = parseFloat(verzuim) || 0;
  const budgetNum = parseFloat(budget) || 0;

  // ROI: (verzuim_user − verzuim_sector)/100 × werkdagen × FTE × dagKosten (alleen positief)
  const potentialSavings = useMemo(() => {
    if (!sectorBenchmark || !fteNum || !verzuimNum) return 0;
    const deltaPct = verzuimNum - sectorBenchmark.verzuim;
    const extraSickDays = (deltaPct / 100) * workingDays * fteNum;
    return Math.max(0, extraSickDays * sectorBenchmark.dagKosten);
  }, [sectorBenchmark, fteNum, verzuimNum, workingDays]);

  const budgetDelta = useMemo(() => {
    if (!sectorBenchmark) return 0;
    return budgetNum - sectorBenchmark.budget;
  }, [sectorBenchmark, budgetNum]);

  const validateStep1 = () => {
    if (!company.trim()) return 'Vul de bedrijfsnaam in.';
    if (!sector) return 'Kies een sector.';
    if (!fte || fteNum <= 0) return 'Vul een geldig aantal FTE in.';
    return '';
  };
  const validateStep2 = () => {
    if (verzuim === '' || verzuimNum < 0 || verzuimNum > 100) return 'Vul een geldig verzuimpercentage in (0–100).';
    if (budget === '' || budgetNum < 0) return 'Vul een geldig vitaliteitsbudget per FTE in.';
    return '';
  };
  const validateStep3 = () => {
    if (!contactName.trim()) return 'Vul je naam in.';
    if (!contactEmail.trim()) return 'Vul je zakelijke e-mailadres in.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail.trim())) return 'Vul een geldig e-mailadres in.';
    return '';
  };

  const handleNext = async () => {
    setError('');
    if (step === 1) { const e = validateStep1(); if (e) { setError(e); return; } }
    if (step === 2) { const e = validateStep2(); if (e) { setError(e); return; } }
    if (step === 3) {
      const e = validateStep3(); if (e) { setError(e); return; }
      // Submit naar backend → maakt lead aan
      const payload = {
        company,
        sector,
        extra_sectors: extraSectors,
        fte: fteNum,
        verzuim_pct: verzuimNum,
        budget_per_fte: budgetNum,
        contact: { name: contactName.trim(), email: contactEmail.trim() },
      };
      // eslint-disable-next-line no-console
      console.log('[Vitaliteit Benchmark 2026] Submission:', payload);
      setSubmitting(true);
      try {
        const res = await vitaliteitBenchmarkAPI.submit(payload);
        // eslint-disable-next-line no-console
        console.log('[Vitaliteit Benchmark 2026] Response:', res.data);
        setSubmissionId(res.data?.submission_id || null);
        toast.success(cfg.thank_you_message || 'Bedankt! We hebben je aanvraag ontvangen.');
        setStep(4);
      } catch (err) {
        const detail = err?.response?.data?.detail || 'Versturen mislukt. Probeer het later opnieuw.';
        setError(detail);
        toast.error(detail);
      } finally {
        setSubmitting(false);
      }
      return;
    }
    setStep(s => s + 1);
  };

  const handleBack = () => { setError(''); setStep(s => Math.max(0, s - 1)); };

  const handleRestart = () => {
    setStep(0); setError('');
    setCompany(''); setSector(''); setExtraSectors([]); setFte('');
    setVerzuim(''); setBudget('');
    setContactName(''); setContactEmail('');
    setSubmissionId(null);
  };

  // ─── PDF Export (utility in /lib/pdfGenerator.js) ─────────────────────────
  const handleDownloadPdf = async () => {
    try {
      await generateBenchmarkPdf({
        company,
        sector,
        fteNum,
        verzuimNum,
        budgetNum,
        sectorBenchmark,
        extraBenchmarks,
        potentialSavings,
        workingDays,
        contactName,
        contactEmail,
        cfg,
        submissionId,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Vitaliteit Benchmark 2026] PDF generation failed:', err);
      toast.error('PDF kon niet worden gegenereerd. Probeer het opnieuw.');
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50/30 py-8 px-4 text-slate-800" data-testid="vitaliteit-benchmark-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <img src={LOGO_ICON} alt="Zynq" className="w-10 h-10 rounded-lg" />
            <div>
              <div className="font-bold text-lg leading-none text-slate-900">Zynq</div>
              <div className="text-xs text-slate-500">Vitaliteit Benchmark 2026</div>
            </div>
          </div>
          {step > 0 && step < 4 && (
            <button
              onClick={handleRestart}
              className="text-xs text-slate-500 hover:text-emerald-700 underline"
            >
              Opnieuw beginnen
            </button>
          )}
        </div>

        {step > 0 && step < 4 && <ProgressBar step={step} />}

        {step === 0 && <IntroStep cfg={cfg} onStart={() => setStep(1)} />}

        {step === 1 && (
          <CompanyStep
            company={company} setCompany={setCompany}
            sector={sector} setSector={setSector}
            extraSectors={extraSectors} setExtraSectors={setExtraSectors}
            fte={fte} setFte={setFte}
            sectorCount={sectorKeys.length}
            onSectorOpen={() => setSectorOpen(true)}
            onExtraSectorsOpen={() => setExtraSectorsOpen(true)}
            onFteHelp={() => setFteHelpOpen(true)}
            onNext={handleNext} error={error}
          />
        )}

        {step === 2 && (
          <MetricsStep
            verzuim={verzuim} setVerzuim={setVerzuim}
            budget={budget} setBudget={setBudget}
            sectorBenchmark={sectorBenchmark}
            onNext={handleNext} onBack={handleBack} error={error}
          />
        )}

        {step === 3 && (
          <ContactStep
            name={contactName} setName={setContactName}
            email={contactEmail} setEmail={setContactEmail}
            ctaSubmit={cfg.cta_submit}
            submitting={submitting}
            onNext={handleNext} onBack={handleBack} error={error}
          />
        )}

        {step === 4 && sectorBenchmark && (
          <DashboardStep
            company={company} sector={sector} fte={fteNum}
            verzuim={verzuimNum} budget={budgetNum}
            sectorBenchmark={sectorBenchmark}
            extraBenchmarks={extraBenchmarks}
            potentialSavings={potentialSavings}
            budgetDelta={budgetDelta}
            roiLabel={cfg.roi_label}
            showPdf={cfg.show_pdf_download !== false}
            workingDays={workingDays}
            submissionId={submissionId}
            onDownloadPdf={handleDownloadPdf}
            onRestart={handleRestart}
            bookingCfg={cfg}
          />
        )}

        <div className="mt-10 text-center text-xs text-slate-500">
          <p>Bron: TNO/CBS SBI-benchmarks · Vitaliteit als missie. © Zynq</p>
        </div>
      </div>

      <SectorPicker open={sectorOpen} setOpen={setSectorOpen} value={sector} onChange={setSector} sectorKeys={sectorKeys} />
      <ExtraSectorsPicker
        open={extraSectorsOpen} setOpen={setExtraSectorsOpen}
        primarySector={sector} selected={extraSectors} onChange={setExtraSectors}
        sectorKeys={sectorKeys}
      />
      <FteHelpModal
        open={fteHelpOpen}
        title={cfg.fte_help_title}
        explainer={cfg.fte_help_explainer}
        onClose={() => setFteHelpOpen(false)}
        onApply={(val) => { setFte(String(val)); setFteHelpOpen(false); }}
      />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
function ProgressBar({ step }) {
  const labels = ['Bedrijf', 'Cijfers', 'Contact'];
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between max-w-md mx-auto">
        {labels.map((lbl, i) => {
          const idx = i + 1;
          const done = step > idx;
          const active = step === idx;
          return (
            <div key={lbl} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                  done ? 'bg-emerald-600 text-white' :
                  active ? 'bg-emerald-600 text-white ring-4 ring-emerald-100' :
                  'bg-gray-200 text-slate-500'
                }`}>
                  {done ? <Check className="w-4 h-4" /> : idx}
                </div>
                <span className={`text-[10px] mt-1 font-medium ${active || done ? 'text-emerald-700' : 'text-slate-400'}`}>{lbl}</span>
              </div>
              {i < labels.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 mb-4 transition-all ${done ? 'bg-emerald-600' : 'bg-gray-200'}`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
function IntroStep({ cfg, onStart }) {
  return (
    <div data-testid="step-intro" className="text-center">
      <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold mb-5">
        <Sparkles className="w-3.5 h-3.5" /> {cfg.intro_badge || 'Nieuw · Benchmark editie 2026'}
      </div>
      <h1 className="text-3xl md:text-5xl font-bold tracking-tight mb-4 text-slate-900">
        {(cfg.intro_title || 'Hoe vitaal is jouw organisatie?')}
      </h1>
      <p className="text-slate-600 max-w-2xl mx-auto mb-8 leading-relaxed whitespace-pre-line">
        {cfg.intro_subtitle}
      </p>

      <div className="grid sm:grid-cols-3 gap-4 max-w-2xl mx-auto mb-10">
        {[
          { Icon: Activity, label: 'Verzuim-score', sub: 'vs. sector' },
          { Icon: Euro, label: 'Budget per FTE', sub: 'vs. sector' },
          { Icon: TrendingUp, label: 'Jaarlijkse ROI', sub: 'potentieel' },
        ].map(({ Icon, label, sub }) => (
          <div key={label} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center mb-3 mx-auto">
              <Icon className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="font-semibold text-slate-900 text-sm">{label}</div>
            <div className="text-xs text-slate-500">{sub}</div>
          </div>
        ))}
      </div>

      <Button onClick={onStart} size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white px-8" data-testid="btn-start">
        {cfg.cta_start || 'Start de benchmark'} <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
      <p className="text-xs text-slate-400 mt-3">Duurt ongeveer 3 minuten · Anoniem & gratis</p>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
function CompanyStep({ company, setCompany, sector, setSector, extraSectors, setExtraSectors, fte, setFte, sectorCount, onSectorOpen, onExtraSectorsOpen, onFteHelp, onNext, error }) {
  const MAX_EXTRA = 3;
  const removeExtra = (s) => setExtraSectors(arr => arr.filter(x => x !== s));
  return (
    <div data-testid="step-company">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold tracking-tight mb-1 text-slate-900">Vertel ons over je organisatie</h2>
        <p className="text-slate-600 text-sm">Drie korte vragen om de juiste benchmark te kiezen.</p>
      </div>

      <Card className="border-slate-200 shadow-sm bg-white">
        <CardContent className="p-6 md:p-8 space-y-6">
          <div>
            <Label htmlFor="company" className="flex items-center gap-2 mb-2">
              <Building className="w-4 h-4 text-emerald-600" /> Bedrijfsnaam
            </Label>
            <Input
              id="company" data-testid="input-company"
              placeholder="Bijv. Zynq B.V."
              value={company} onChange={(e) => setCompany(e.target.value)}
            />
          </div>

          <div>
            <Label className="flex items-center gap-2 mb-2">
              <BarChart3 className="w-4 h-4 text-emerald-600" /> Sector (SBI 2026)
            </Label>
            <button
              type="button" data-testid="btn-sector"
              onClick={onSectorOpen}
              className="w-full flex items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm hover:border-emerald-400 transition-colors"
            >
              <span className={sector ? 'text-slate-900' : 'text-slate-400'}>
                {sector || 'Kies je sector…'}
              </span>
              <ChevronsUpDown className="w-4 h-4 text-slate-400" />
            </button>
            <p className="text-xs text-slate-500 mt-1">Doorzoekbaar · {sectorCount || 21} sectoren beschikbaar</p>
          </div>

          {/* Extra sectoren vergelijken (optioneel) */}
          {sector && (
            <div className="rounded-lg border border-emerald-100 bg-emerald-50/40 p-4">
              <div className="flex items-center justify-between mb-2">
                <Label className="flex items-center gap-2 text-emerald-900">
                  <BarChart3 className="w-4 h-4" /> Vergelijk ook met andere sectoren <span className="text-xs font-normal text-emerald-700">(optioneel · max {MAX_EXTRA})</span>
                </Label>
                <span className="text-xs text-emerald-700 font-medium">{extraSectors.length}/{MAX_EXTRA}</span>
              </div>
              {extraSectors.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {extraSectors.map(s => (
                    <span key={s} className="inline-flex items-center gap-1.5 bg-white border border-emerald-200 text-slate-700 text-xs rounded-full pl-2.5 pr-1 py-1" data-testid={`chip-${s.split(':')[0]}`}>
                      <span className="font-medium">{s.split(':')[0]}</span>
                      <span className="text-slate-500 truncate max-w-[140px]">{s.split(':')[1]?.trim()}</span>
                      <button onClick={() => removeExtra(s)} className="ml-1 p-0.5 rounded-full hover:bg-slate-100">
                        <X className="w-3 h-3 text-slate-500" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <button
                type="button" onClick={onExtraSectorsOpen}
                disabled={extraSectors.length >= MAX_EXTRA}
                data-testid="btn-extra-sectors"
                className="w-full text-xs text-emerald-700 hover:text-emerald-800 font-semibold inline-flex items-center justify-center gap-1 py-1.5 rounded-md border border-dashed border-emerald-300 hover:bg-emerald-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Sparkles className="w-3.5 h-3.5" /> {extraSectors.length === 0 ? 'Sectoren ter vergelijking toevoegen' : 'Meer sectoren toevoegen'}
              </button>
              <p className="text-[11px] text-emerald-800/70 mt-1.5">Handig voor holdings of organisaties die in meerdere SBI-categorieën opereren.</p>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label htmlFor="fte" className="flex items-center gap-2">
                <Users className="w-4 h-4 text-emerald-600" /> Aantal FTE
              </Label>
              <button
                type="button" data-testid="btn-fte-help"
                onClick={onFteHelp}
                className="text-xs text-emerald-700 hover:text-emerald-800 font-semibold inline-flex items-center gap-1"
              >
                <Calculator className="w-3.5 h-3.5" /> Hulp bij berekenen
              </button>
            </div>
            <Input
              id="fte" data-testid="input-fte"
              type="number" min="0" step="0.1"
              placeholder="Bijv. 25"
              value={fte} onChange={(e) => setFte(e.target.value)}
            />
            <p className="text-xs text-slate-500 mt-1">FTE = Full-Time Equivalent. Twee parttimers van 20u = 1 FTE.</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /> {error}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end mt-6">
        <Button onClick={onNext} size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-next-1">
          Volgende <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
function MetricsStep({ verzuim, setVerzuim, budget, setBudget, sectorBenchmark, onNext, onBack, error }) {
  return (
    <div data-testid="step-metrics">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold tracking-tight mb-1 text-slate-900">Hoe staan jullie ervoor?</h2>
        <p className="text-slate-600 text-sm">Vul je huidige verzuim en vitaliteitsbudget in om de vergelijking te maken.</p>
      </div>

      <Card className="border-slate-200 shadow-sm bg-white">
        <CardContent className="p-6 md:p-8 space-y-6">
          <div>
            <Label htmlFor="verzuim" className="flex items-center gap-2 mb-2">
              <Activity className="w-4 h-4 text-emerald-600" /> Huidig verzuimpercentage (%)
            </Label>
            <div className="relative">
              <Input
                id="verzuim" data-testid="input-verzuim"
                type="number" min="0" max="100" step="0.1"
                placeholder="Bijv. 5.2"
                value={verzuim} onChange={(e) => setVerzuim(e.target.value)}
                className="pr-10"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
            </div>
            {sectorBenchmark && (
              <p className="text-xs text-slate-500 mt-1">
                Sectorgemiddelde: <span className="font-semibold text-emerald-700">{fmtPct(sectorBenchmark.verzuim)}</span>
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="budget" className="flex items-center gap-2 mb-2">
              <Euro className="w-4 h-4 text-emerald-600" /> Vitaliteitsbudget per FTE (€/jaar)
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
              <Input
                id="budget" data-testid="input-budget"
                type="number" min="0" step="10"
                placeholder="Bijv. 350"
                value={budget} onChange={(e) => setBudget(e.target.value)}
                className="pl-7"
              />
            </div>
            {sectorBenchmark && (
              <p className="text-xs text-slate-500 mt-1">
                Sectorgemiddelde: <span className="font-semibold text-emerald-700">{fmtCur(sectorBenchmark.budget)}</span> per FTE
              </p>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /> {error}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-between mt-6">
        <Button onClick={onBack} variant="outline" size="lg" data-testid="btn-back-2">
          <ArrowLeft className="w-4 h-4 mr-2" /> Terug
        </Button>
        <Button onClick={onNext} size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-next-2">
          Volgende <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
function ContactStep({ name, setName, email, setEmail, ctaSubmit, submitting, onNext, onBack, error }) {
  return (
    <div data-testid="step-contact">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold tracking-tight mb-1 text-slate-900">Bijna klaar — waar mogen we het rapport sturen?</h2>
        <p className="text-slate-600 text-sm">Je rapport verschijnt direct in beeld en is ook downloadbaar als PDF.</p>
      </div>

      <Card className="border-slate-200 shadow-sm bg-white">
        <CardContent className="p-6 md:p-8 space-y-6">
          <div>
            <Label htmlFor="name" className="flex items-center gap-2 mb-2">
              <User className="w-4 h-4 text-emerald-600" /> Jouw naam
            </Label>
            <Input id="name" data-testid="input-name" placeholder="Voor- en achternaam"
              value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="email" className="flex items-center gap-2 mb-2">
              <Mail className="w-4 h-4 text-emerald-600" /> Zakelijk e-mailadres
            </Label>
            <Input id="email" data-testid="input-email" type="email" placeholder="jij@bedrijf.nl"
              value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /> {error}
            </div>
          )}

          <p className="text-xs text-slate-500">
            Door op "Toon resultaten" te klikken ga je akkoord met het opslaan van je gegevens
            ten behoeve van dit benchmark-rapport.
          </p>
        </CardContent>
      </Card>

      <div className="flex justify-between mt-6">
        <Button onClick={onBack} variant="outline" size="lg" data-testid="btn-back-3">
          <ArrowLeft className="w-4 h-4 mr-2" /> Terug
        </Button>
        <Button onClick={onNext} size="lg" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-submit">
          {submitting ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Versturen…</>
          ) : (
            <>{ctaSubmit || 'Toon resultaten'} <CheckCircle2 className="w-4 h-4 ml-2" /></>
          )}
        </Button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
function DashboardStep({ company, sector, fte, verzuim, budget, sectorBenchmark, extraBenchmarks = [], potentialSavings, budgetDelta, roiLabel, showPdf, workingDays, submissionId, onDownloadPdf, onRestart, bookingCfg }) {
  const verzuimWorse = verzuim > sectorBenchmark.verzuim;
  const budgetBelow = budget < sectorBenchmark.budget;
  const hasExtras = extraBenchmarks.length > 0;

  return (
    <div data-testid="step-dashboard" className="space-y-6">
      {/* Header card */}
      <Card className="border-emerald-200 shadow-sm bg-gradient-to-br from-white to-emerald-50/40">
        <CardContent className="p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold mb-2">
                <Award className="w-3.5 h-3.5" /> Benchmark-rapport
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900">{company}</h2>
              <p className="text-sm text-slate-600 mt-1">{sector}</p>
              <p className="text-xs text-slate-500 mt-1">{fmtNum(fte)} FTE · {new Date().toLocaleDateString('nl-NL')}</p>
            </div>
            <div className="flex gap-2">
              {showPdf !== false && (
                <Button onClick={onDownloadPdf} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-download-pdf">
                  <Download className="w-4 h-4 mr-2" /> PDF downloaden
                </Button>
              )}
              <Button onClick={onRestart} variant="outline">Opnieuw</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ROI highlight */}
      <Card className="border-2 border-emerald-300 shadow-lg bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
        <CardContent className="p-6 md:p-8">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-7 h-7" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-emerald-50 uppercase tracking-wide">{roiLabel || 'Jaarlijkse potentiële besparing'}</div>
              <div className="text-4xl md:text-5xl font-bold mt-1" data-testid="roi-amount">{fmtCur(potentialSavings)}</div>
              <p className="text-sm text-emerald-50 mt-2 leading-relaxed">
                {potentialSavings > 0 ? (
                  <>Door je verzuim van {fmtPct(verzuim)} terug te brengen naar het sectorgemiddelde van {fmtPct(sectorBenchmark.verzuim)} bespaar je jaarlijks circa <strong>{fmtCur(potentialSavings)}</strong> op verzuimkosten ({fmtCur(sectorBenchmark.dagKosten)} per dag × ± {fmtNum(((verzuim - sectorBenchmark.verzuim) / 100) * (workingDays || 230) * fte)} extra ziekdagen/jaar).</>
                ) : (
                  <>Goed nieuws! Je verzuim ligt op of onder het sectorgemiddelde. Focus je nu op preventie en duurzame inzetbaarheid voor structurele winst.</>
                )}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Boek een meeting CTA */}
      <BookingCta config={bookingCfg} variant="emerald" />

      {/* Gauges */}
      <div className="grid md:grid-cols-3 gap-4">
        <GaugeCard
          title="Verzuim"
          value={verzuim}
          benchmark={sectorBenchmark.verzuim}
          unit="%"
          max={Math.max(10, sectorBenchmark.verzuim * 1.5, verzuim * 1.2)}
          better="lower"
          color={verzuimWorse ? '#ef4444' : '#10b981'}
          Icon={Activity}
        />
        <GaugeCard
          title="Budget per FTE"
          value={budget}
          benchmark={sectorBenchmark.budget}
          unit="€"
          max={Math.max(sectorBenchmark.budget * 1.5, budget * 1.2, 500)}
          better="higher"
          color={budgetBelow ? '#f59e0b' : '#10b981'}
          Icon={Euro}
        />
        <GaugeCard
          title="Burn-out risico sector"
          value={sectorBenchmark.burnout}
          benchmark={sectorBenchmark.burnout}
          unit="%"
          max={35}
          better="lower"
          color="#0d9488"
          Icon={Heart}
          contextLabel="sector-cijfer"
        />
      </div>

      {/* Comparison chart */}
      <Card className="border-slate-200 shadow-sm bg-white">
        <CardContent className="p-6 md:p-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-lg text-slate-900">
                {hasExtras ? 'Vergelijking met meerdere sectoren' : 'Vergelijking met sectorgemiddelde'}
              </h3>
              <p className="text-xs text-slate-500">
                {hasExtras
                  ? `Jouw cijfers naast ${1 + extraBenchmarks.length} sectoren.`
                  : `Hoe verhouden jouw cijfers zich tot ${sector.split(':')[1]?.trim() || sector}?`}
              </p>
            </div>
            <Target className="w-5 h-5 text-emerald-600" />
          </div>

          {/* Verzuim chart */}
          <div className="mb-2 text-xs font-semibold text-slate-600 uppercase tracking-wide">Verzuim (%)</div>
          <div className="h-56" data-testid="chart-verzuim">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={[
                  { name: 'Jouw bedrijf', value: verzuim, fill: '#10b981' },
                  { name: sector.split(':')[0], value: sectorBenchmark.verzuim, fill: '#475569' },
                  ...extraBenchmarks.map((b, i) => ({
                    name: b.sector.split(':')[0],
                    value: b.verzuim,
                    fill: ['#a78bfa', '#f97316', '#0ea5e9'][i % 3],
                  })),
                ]}
                margin={{ top: 10, right: 16, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: '#475569', fontSize: 12 }} axisLine={{ stroke: '#cbd5e1' }} />
                <YAxis tick={{ fill: '#475569', fontSize: 12 }} axisLine={{ stroke: '#cbd5e1' }} unit="%" />
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }} formatter={(v) => `${Number(v).toFixed(1)}%`} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {[
                    { fill: '#10b981' },
                    { fill: '#475569' },
                    ...extraBenchmarks.map((_, i) => ({ fill: ['#a78bfa', '#f97316', '#0ea5e9'][i % 3] })),
                  ].map((c, idx) => (
                    <Cell key={idx} fill={c.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Budget chart */}
          <div className="mt-6 mb-2 text-xs font-semibold text-slate-600 uppercase tracking-wide">Budget per FTE (€)</div>
          <div className="h-56" data-testid="chart-budget">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={[
                  { name: 'Jouw bedrijf', value: budget, fill: '#10b981' },
                  { name: sector.split(':')[0], value: sectorBenchmark.budget, fill: '#475569' },
                  ...extraBenchmarks.map((b, i) => ({
                    name: b.sector.split(':')[0],
                    value: b.budget,
                    fill: ['#a78bfa', '#f97316', '#0ea5e9'][i % 3],
                  })),
                ]}
                margin={{ top: 10, right: 16, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: '#475569', fontSize: 12 }} axisLine={{ stroke: '#cbd5e1' }} />
                <YAxis tick={{ fill: '#475569', fontSize: 12 }} axisLine={{ stroke: '#cbd5e1' }} />
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }} formatter={(v) => fmtCur(v)} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {[
                    { fill: '#10b981' },
                    { fill: '#475569' },
                    ...extraBenchmarks.map((_, i) => ({ fill: ['#a78bfa', '#f97316', '#0ea5e9'][i % 3] })),
                  ].map((c, idx) => (
                    <Cell key={idx} fill={c.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            <InsightTile
              good={!verzuimWorse}
              label="Verzuim"
              value={fmtPct(verzuim)}
              compareValue={fmtPct(sectorBenchmark.verzuim)}
              text={verzuimWorse
                ? `Jouw verzuim ligt ${fmtPct(verzuim - sectorBenchmark.verzuim)} hoger dan sector.`
                : `Jouw verzuim ligt ${fmtPct(sectorBenchmark.verzuim - verzuim)} lager dan sector.`}
            />
            <InsightTile
              good={!budgetBelow}
              label="Vitaliteitsbudget"
              value={fmtCur(budget)}
              compareValue={fmtCur(sectorBenchmark.budget)}
              text={budgetBelow
                ? `Je investeert ${fmtCur(sectorBenchmark.budget - budget)} minder per FTE dan sector.`
                : `Je investeert ${fmtCur(budget - sectorBenchmark.budget)} meer per FTE dan sector.`}
            />
          </div>
        </CardContent>
      </Card>

      {/* Multi-sector detail table (only when extras present) */}
      {hasExtras && (
        <Card className="border-slate-200 shadow-sm bg-white" data-testid="multi-sector-table">
          <CardContent className="p-6 md:p-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-lg text-slate-900">Sector-detailtabel</h3>
                <p className="text-xs text-slate-500">Volledige cijfers per sector inclusief burn-out en dagkosten.</p>
              </div>
              <BarChart3 className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                    <th className="pb-2 pr-3 font-semibold">Bron</th>
                    <th className="pb-2 pr-3 font-semibold">Verzuim</th>
                    <th className="pb-2 pr-3 font-semibold">Budget/FTE</th>
                    <th className="pb-2 pr-3 font-semibold">Burn-out</th>
                    <th className="pb-2 font-semibold">Dagkosten</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-100 bg-emerald-50/40">
                    <td className="py-2.5 pr-3 font-semibold text-emerald-800">{company || 'Jouw bedrijf'}</td>
                    <td className="py-2.5 pr-3 font-bold text-slate-900">{fmtPct(verzuim)}</td>
                    <td className="py-2.5 pr-3 font-bold text-slate-900">{fmtCur(budget)}</td>
                    <td className="py-2.5 pr-3 text-slate-400">-</td>
                    <td className="py-2.5 text-slate-400">-</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="py-2.5 pr-3 font-medium text-slate-700">
                      <span className="inline-block w-2 h-2 rounded-full bg-slate-500 mr-2" />
                      {sector.split(':')[0]} <span className="text-xs text-slate-500">(primair)</span>
                    </td>
                    <td className="py-2.5 pr-3 text-slate-700">{fmtPct(sectorBenchmark.verzuim)}</td>
                    <td className="py-2.5 pr-3 text-slate-700">{fmtCur(sectorBenchmark.budget)}</td>
                    <td className="py-2.5 pr-3 text-slate-700">{sectorBenchmark.burnout}%</td>
                    <td className="py-2.5 text-slate-700">{fmtCur(sectorBenchmark.dagKosten)}</td>
                  </tr>
                  {extraBenchmarks.map((b, i) => (
                    <tr key={b.sector} className="border-b border-slate-100 last:border-b-0">
                      <td className="py-2.5 pr-3 font-medium text-slate-700">
                        <span
                          className="inline-block w-2 h-2 rounded-full mr-2"
                          style={{ backgroundColor: ['#a78bfa', '#f97316', '#0ea5e9'][i % 3] }}
                        />
                        {b.sector.split(':')[0]}
                        <span className="text-xs text-slate-500 ml-1.5">{b.sector.split(':')[1]?.trim()}</span>
                      </td>
                      <td className="py-2.5 pr-3 text-slate-700">{fmtPct(b.verzuim)}</td>
                      <td className="py-2.5 pr-3 text-slate-700">{fmtCur(b.budget)}</td>
                      <td className="py-2.5 pr-3 text-slate-700">{b.burnout}%</td>
                      <td className="py-2.5 text-slate-700">{fmtCur(b.dagKosten)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {submissionId && (
              <p className="text-[11px] text-slate-400 mt-3">Rapport-ID: {submissionId}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Burn-out context */}
      <Card className="border-slate-200 shadow-sm bg-white">
        <CardContent className="p-6 md:p-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-rose-100 flex items-center justify-center flex-shrink-0">
              <Heart className="w-6 h-6 text-rose-600" />
            </div>
            <div>
              <h3 className="font-bold text-lg text-slate-900 mb-1">Burn-out risico in jouw sector</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Gemiddeld <span className="font-bold text-rose-600">{sectorBenchmark.burnout}%</span> van de werknemers in de sector
                <span className="font-semibold"> {sector.split(':')[1]?.trim() || sector}</span> ervaart verhoogde burn-out klachten.
                Investeer in mentale veerkracht, leiderschap en herstel om dit aandeel binnen jouw organisatie te verlagen.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {showPdf !== false && (
        <div className="flex justify-center pt-2">
          <Button onClick={onDownloadPdf} size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-download-pdf-bottom">
            <Download className="w-4 h-4 mr-2" /> Download volledig PDF-rapport
          </Button>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
function GaugeCard({ title, value, benchmark, unit, max, better, color, Icon, contextLabel }) {
  const pct = Math.min(100, (Number(value) / max) * 100);
  const data = [{ name: title, value: pct, fill: color }];
  const benchmarkPct = Math.min(100, (Number(benchmark) / max) * 100);

  return (
    <Card className="border-slate-200 shadow-sm bg-white">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-slate-500" />
            <h4 className="font-semibold text-sm text-slate-700">{title}</h4>
          </div>
          {contextLabel && <span className="text-[10px] uppercase tracking-wide text-slate-400">{contextLabel}</span>}
        </div>
        <div className="h-32 relative">
          <ResponsiveContainer width="100%" height="100%">
            <RadialBarChart innerRadius="70%" outerRadius="100%" data={data} startAngle={180} endAngle={0}>
              <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
              <RadialBar background={{ fill: '#f1f5f9' }} dataKey="value" cornerRadius={10} />
            </RadialBarChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-end pb-2">
            <div className="text-2xl font-bold text-slate-900">
              {unit === '€' ? fmtCur(value) : `${fmtNum(value)}${unit}`}
            </div>
            <div className="text-[10px] uppercase tracking-wide text-slate-400">jouw cijfer</div>
          </div>
        </div>
        <div className="flex items-center justify-between text-xs mt-3 pt-3 border-t border-slate-100">
          <div className="text-slate-500">Sector: <span className="font-semibold text-slate-700">{unit === '€' ? fmtCur(benchmark) : `${fmtNum(benchmark)}${unit}`}</span></div>
          {Number(value) !== Number(benchmark) && better && (
            (better === 'lower' ? value < benchmark : value > benchmark) ? (
              <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold"><TrendingDown className="w-3 h-3" /> beter</span>
            ) : (
              <span className="inline-flex items-center gap-1 text-amber-600 font-semibold"><TrendingUp className="w-3 h-3" /> aandacht</span>
            )
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function InsightTile({ good, label, value, compareValue, text }) {
  return (
    <div className={`rounded-xl border p-4 ${good ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
      <div className="flex items-center gap-2 mb-1">
        {good ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-amber-600" />}
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">{label}</span>
      </div>
      <div className="text-lg font-bold text-slate-900">{value} <span className="text-xs text-slate-400 font-normal">vs {compareValue}</span></div>
      <p className={`text-xs mt-1 leading-relaxed ${good ? 'text-emerald-700' : 'text-amber-700'}`}>{text}</p>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
function ExtraSectorsPicker({ open, setOpen, primarySector, selected, onChange, sectorKeys }) {
  const [query, setQuery] = useState('');
  const keys = sectorKeys && sectorKeys.length ? sectorKeys : Object.keys(FALLBACK_BENCHMARKS);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return keys.filter(k => k !== primarySector && (!q || k.toLowerCase().includes(q)));
  }, [query, keys, primarySector]);
  const MAX = 3;
  const toggle = (k) => {
    if (selected.includes(k)) onChange(selected.filter(x => x !== k));
    else if (selected.length < MAX) onChange([...selected, k]);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg p-0 overflow-hidden">
        <DialogHeader className="px-5 pt-5">
          <DialogTitle>Sectoren ter vergelijking</DialogTitle>
          <DialogDescription>Kies tot {MAX} aanvullende sectoren om naast je primaire sector mee te vergelijken.</DialogDescription>
        </DialogHeader>
        <div className="px-5 pt-3 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              autoFocus data-testid="input-extra-sector-search"
              placeholder="Zoek sector…"
              value={query} onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="text-[11px] text-slate-500 mt-1.5">{selected.length}/{MAX} gekozen</div>
        </div>
        <div className="max-h-80 overflow-y-auto px-2 pb-3">
          {filtered.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">Geen sector gevonden.</div>
          ) : (
            filtered.map((k) => {
              const isOn = selected.includes(k);
              const disabled = !isOn && selected.length >= MAX;
              return (
                <button
                  key={k} onClick={() => toggle(k)} disabled={disabled}
                  data-testid={`extra-option-${k.split(':')[0]}`}
                  className={`w-full text-left flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
                    isOn ? 'bg-emerald-50 text-emerald-700' : 'text-slate-700 hover:bg-slate-50'
                  } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                >
                  <span>{k}</span>
                  {isOn && <Check className="w-4 h-4 text-emerald-600" />}
                </button>
              );
            })
          )}
        </div>
        <DialogFooter className="px-5 pb-4 pt-2 gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => { onChange([]); }}>Wissen</Button>
          <Button onClick={() => setOpen(false)} className="bg-emerald-600 hover:bg-emerald-700 text-white">Klaar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SectorPicker({ open, setOpen, value, onChange, sectorKeys }) {
  const [query, setQuery] = useState('');
  const keys = sectorKeys && sectorKeys.length ? sectorKeys : Object.keys(FALLBACK_BENCHMARKS);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return keys;
    return keys.filter(k => k.toLowerCase().includes(q));
  }, [query, keys]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg p-0 overflow-hidden">
        <DialogHeader className="px-5 pt-5">
          <DialogTitle>Kies je sector</DialogTitle>
          <DialogDescription>Selecteer de SBI-categorie die het beste past bij je organisatie.</DialogDescription>
        </DialogHeader>
        <div className="px-5 pt-3 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              autoFocus data-testid="input-sector-search"
              placeholder="Zoek sector (bv. zorg, onderwijs, IT)…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
            />
            {query && (
              <button onClick={() => setQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100">
                <X className="w-3.5 h-3.5 text-slate-400" />
              </button>
            )}
          </div>
        </div>
        <div className="max-h-80 overflow-y-auto px-2 pb-3">
          {filtered.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">Geen sector gevonden.</div>
          ) : (
            filtered.map((k) => {
              const selected = k === value;
              return (
                <button
                  key={k}
                  onClick={() => { onChange(k); setOpen(false); setQuery(''); }}
                  data-testid={`sector-option-${k.split(':')[0]}`}
                  className={`w-full text-left flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
                    selected ? 'bg-emerald-50 text-emerald-700' : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <span>{k}</span>
                  {selected && <Check className="w-4 h-4 text-emerald-600" />}
                </button>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────────────
function FteHelpModal({ open, title, explainer, onClose, onApply }) {
  const [employees, setEmployees] = useState('');
  const [avgHours, setAvgHours] = useState('40');
  const [fulltimeHours, setFulltimeHours] = useState('40');

  const calculated = useMemo(() => {
    const e = parseFloat(employees) || 0;
    const a = parseFloat(avgHours) || 0;
    const f = parseFloat(fulltimeHours) || 40;
    if (!e || !a || !f) return 0;
    return (e * a) / f;
  }, [employees, avgHours, fulltimeHours]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-emerald-600" /> {title || 'Hulp bij berekenen FTE'}
          </DialogTitle>
          <DialogDescription className="whitespace-pre-line">
            {explainer || 'FTE staat voor Full-Time Equivalent: totaal aantal contracturen gedeeld door een volledige werkweek (meestal 40 uur).'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-900 leading-relaxed flex gap-2">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              <strong>Voorbeeld:</strong> 10 medewerkers werken gemiddeld 32 uur per week.
              Bij een fulltime week van 40 uur: <em>(10 × 32) / 40 = 8 FTE</em>.
            </span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="emp" className="text-xs">Aantal medewerkers</Label>
              <Input id="emp" data-testid="fte-employees" type="number" min="0"
                value={employees} onChange={(e) => setEmployees(e.target.value)} placeholder="Bijv. 25" />
            </div>
            <div>
              <Label htmlFor="avg" className="text-xs">Gem. uren / week</Label>
              <Input id="avg" data-testid="fte-avg-hours" type="number" min="0" max="80" step="0.5"
                value={avgHours} onChange={(e) => setAvgHours(e.target.value)} />
            </div>
          </div>
          <div>
            <Label htmlFor="ft" className="text-xs">Volledige werkweek (uren)</Label>
            <Input id="ft" data-testid="fte-fulltime" type="number" min="20" max="60" step="0.5"
              value={fulltimeHours} onChange={(e) => setFulltimeHours(e.target.value)} />
            <p className="text-[11px] text-slate-500 mt-1">Standaard 36–40 uur per week in Nederland.</p>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-center">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Berekende FTE</div>
            <div className="text-3xl font-bold text-emerald-600" data-testid="fte-result">{fmtNum(calculated)}</div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
          <Button
            onClick={() => onApply(calculated.toFixed(2))}
            disabled={!calculated}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="btn-fte-apply"
          >
            <Check className="w-4 h-4 mr-2" /> Gebruik {fmtNum(calculated)} FTE
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


